// Typed fetch wrapper + single-flight refresh on 401.
//
// Token storage:
//   - access token lives in a module-level variable (NOT localStorage).
//     XSS that runs in our origin cannot exfiltrate it via storage
//     enumeration. Refresh on every reload via the httpOnly cookie set
//     by `/auth/login?client_type=admin` (see backend PR3 changes).
//   - refresh token NEVER touches JS — it's in an httpOnly+Secure+SameSite
//     cookie scoped to `/api/auth/refresh`. The browser sends it
//     automatically; JS cannot read it.
//
// 401 flow:
//   1. Request returns 401.
//   2. We call POST /api/auth/refresh (browser attaches the cookie).
//   3. Single-flight: concurrent 401s share the same in-flight refresh.
//   4. On success, we retry the original request once with the new token.
//   5. On failure, we clear the in-memory access token and emit an
//      `auth:invalidated` event so the AuthProvider can route to Login.
//
// Mirrors the iOS APIClient pattern documented in ios-runtime.md.

import type { APIErrorEnvelope } from '@/types/api'

const BASE = '/api'

let accessToken: string | null = null
let refreshPromise: Promise<boolean> | null = null

const listeners = new Set<() => void>()

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

export function onAuthInvalidated(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

function emitInvalidated(): void {
  accessToken = null
  for (const cb of listeners) cb()
}

export class APIError extends Error {
  readonly status: number
  readonly code: string
  readonly context?: Record<string, unknown>
  constructor(status: number, envelope: APIErrorEnvelope['error'] | undefined, fallback: string) {
    super(envelope?.message ?? fallback)
    this.status = status
    this.code = envelope?.code ?? 'UNKNOWN'
    this.context = envelope?.context
  }
}

async function performRefresh(): Promise<boolean> {
  try {
    const r = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',  // send the httpOnly refresh cookie
      headers: { 'Content-Type': 'application/json' },
      // Body intentionally empty — the backend prefers the cookie when
      // both cookie and body are present for the admin path.
      body: JSON.stringify({}),
    })
    if (!r.ok) return false
    const data = (await r.json()) as { access_token: string }
    if (!data?.access_token) return false
    accessToken = data.access_token
    return true
  } catch {
    return false
  }
}

async function ensureRefreshed(): Promise<boolean> {
  if (refreshPromise) return refreshPromise
  refreshPromise = performRefresh().finally(() => {
    refreshPromise = null
  })
  return refreshPromise
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

interface RequestOptions {
  method?: Method
  body?: unknown
  query?: Record<string, string | number | undefined | null>
  // `canRefresh=false` is set internally to break the recursion on the
  // retry attempt — never set it from a caller.
  canRefresh?: boolean
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  let url = `${BASE}${path}`
  if (query) {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v))
    }
    const qs = params.toString()
    if (qs) url += `?${qs}`
  }
  return url
}

export async function request<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, query, canRefresh = true } = options
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const res = await fetch(buildUrl(path, query), {
    method,
    headers,
    credentials: 'include',  // refresh cookie travels along
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (res.status === 401 && canRefresh) {
    const ok = await ensureRefreshed()
    if (!ok) {
      emitInvalidated()
      throw new APIError(401, undefined, 'Unauthorized')
    }
    return request<T>(path, { ...options, canRefresh: false })
  }

  if (!res.ok) {
    let envelope: APIErrorEnvelope | undefined
    try { envelope = (await res.json()) as APIErrorEnvelope } catch { /* swallow */ }
    throw new APIError(res.status, envelope?.error, `Request failed (${res.status})`)
  }

  // 204 No Content
  if (res.status === 204) return undefined as T

  return (await res.json()) as T
}

// Multipart file upload (PX-247/PX-248). Separate from `request` because the
// browser MUST set the multipart boundary Content-Type itself — we never set
// it by hand. Same single-flight 401-refresh + retry-once contract as `request`.
export async function uploadFile<T = unknown>(
  path: string,
  file: File,
  canRefresh = true,
): Promise<T> {
  const form = new FormData()
  form.append('file', file)

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  // NO Content-Type — fetch derives the multipart boundary from the FormData.

  const res = await fetch(buildUrl(path), {
    method: 'POST',
    headers,
    credentials: 'include',
    body: form,
  })

  if (res.status === 401 && canRefresh) {
    const ok = await ensureRefreshed()
    if (!ok) {
      emitInvalidated()
      throw new APIError(401, undefined, 'Unauthorized')
    }
    return uploadFile<T>(path, file, false)
  }

  if (!res.ok) {
    let envelope: APIErrorEnvelope | undefined
    try { envelope = (await res.json()) as APIErrorEnvelope } catch { /* swallow */ }
    throw new APIError(res.status, envelope?.error, `Upload failed (${res.status})`)
  }

  return (await res.json()) as T
}
